import {
  ENVS,
  getEnv,
  REGIONS,
  getRegion,
} from '@adsk/acs-sc-web-platform-api';
import { EntitlementsProject } from '@adsk/alloy-provider';
import { NOTIFICATION_STATUSES } from '@adsk/alloy-react-notification';

export const SCROLL_CONTAINER_ID = 'scroll-to-top';
export const NO_PERMISSIONS_HELP_ARTICLE =
  'https://help.autodesk.com/view/DOCS/ENU/?guid=Manage_Account_Members';
export const CONTACT_SUPPORT_LINK =
  'https://beta.downloads.autodesk.com/ACS/Build/GG%20Webform%20HTML%20Full%20Page%20v1.html';

export const ACCOUNT_ADMIN_PRODUCT_NAME = 'Account Admin';
export const INSIGHT_PRODUCT_NAME = 'Executive Insight';

export const AccountAdminProductName = 'Account Administration';
export const InsightProductName = 'Insight';

export enum FEATURE_FLAGS {
  ShowAutoSpecsLink = 'pl_show_autospecs_link',
  SetTitlesForModals = 'pl-use-tab-title-component',
  SlashClient = 'slash-clients',
  MfaSetupRequired = 'mfa-setup-required',
  ToolEntryFlow = 'pl-tool-entry-flow',
}

export enum LOCAL_STORAGE_KEYS {
  TableColumnOrder = 'acc_project_list_table_column_order',
  TableColumnVisibility = 'acc_project_list_table_column_visibility',
  TableSize = 'acc_project_list_table_size',
  TableProductEntryFlow = 'acc_project_list_table_product_entry_flow',
}

export const PAGE_HEADER_HEIGHT = '285px';

export const MAPBOX_ACCESS_TOKEN =
  'pk.eyJ1IjoiYnVpbGRpbmdjb25uZWN0ZWQiLCJhIjoiY2tiY3dsNGN6MDV6eTJxcGpsNzdlZGVlYiJ9.IZRpOd2-B-S9izsmviavGA';
export const DEFAULT_SEARCH_KEYS: (keyof EntitlementsProject)[] = ['name'];
export const DEFAULT_SORT_FIELD = 'createdAt';
export const DEFAULT_LIMIT = 25;
export const DEFAULT_SEARCH_BAR_DELAY = 200;
export const PRODUCT_NAME_MAX_LENGTH = 15;
export enum TABLE_SORT_DIRECTION {
  ASCENDING = 1,
  DESCENDING = -1,
}

export enum SORT_DIRECTION {
  ASCENDING = 'asc',
  DESCENDING = 'desc',
}

export const COLUMNS_IGNORE_LIST = ['actions', 'name', 'products'];

export const SIDE_FILTERS_KEYS = ['account', 'project_type', 'created_on'];

export const AnalyticsEvents = {
  navigateToOtherProduct: 'prodex.project_list_page.navigate.product.click',
  navigateToNoPermissionsHelpArticle:
    'prodex.project_list_page.navigate.no_permissions_help_article',

  //Projects Tab
  viewProjects: 'prodex.project_list_page.projects.view',
  projectsNavigateToProject:
    'prodex.project_list_page.projects.project.navigate',
  createProjectModalOpen:
    'prodex.project_list_page.projects.create_project.open_modal',
  createProjectModalClose:
    'prodex.project_list_page.projects.create_project.cancel',
  createProjectModalSubmit:
    'prodex.project_list_page.projects.create_project.submit',
  projectsTableSortColumn: 'prodex.project_list_page.projects.sort.change',
  projectsSearchBarChange: 'prodex.project_list_page.projects.search.change',
  projectsPaginationChange:
    'prodex.project_list_page.projects.pagination.change',

  // Templates Tab
  viewTemplates: 'prodex.project_list_page.templates.view',
  templatesNavigateToProject:
    'prodex.project_list_page.templates.project.navigate',
  createProjectTemplateModalOpen:
    'prodex.project_list_page.templates.create_template.open_modal',
  createProjectTemplateModalClose:
    'prodex.project_list_page.templates.create_template.cancel',
  createProjectTemplateModalSubmit:
    'prodex.project_list_page.templates.create_template.submit',
  templatesTableSortColumn: 'prodex.project_list_page.templates.sort.change',
  templatesSearchBarChange: 'prodex.project_list_page.templates.search.change',
  templatesPaginationChange:
    'prodex.project_list_page.templates.pagination.change',

  //Filters
  filtersPanelToggle: 'prodex.project_list_page.filter.filter_button.click',
  clearFilters: 'prodex.project_list_page.filter.clear',
  addFilter: 'prodex.project_list_page.filter.filter_item.added',
  removeFilter: 'prodex.project_list_page.filter.filter_item.removed',

  //Product Entry Flow
  productEntryFlowOpen:
    'prodex.project_list_page.quick_access.product_list.open',
  productEntryFlowNavigate:
    'prodex.project_list_page.quick_access.product.click',
  quickAccessOpenInTab: 'prodex.project_list_page.quick_access.open_in_tab',
};

export const ErrorNotificationBaseProps = {
  status: NOTIFICATION_STATUSES.ERROR,
  autoDismiss: 0,
  showIcon: true,
};

export const SuccessNotificationBaseProps = {
  status: NOTIFICATION_STATUSES.SUCCESS,
  autoDismiss: 5,
  showIcon: true,
};

export enum Status {
  Active = 'active',
  Pending = 'pending',
  Suspended = 'suspended',
  Archived = 'archived',
}

export const BUILDING_CONNECTED_URLS = {
  [ENVS.QA]: 'https://app-dev.buildingconnected.com/',
  [ENVS.STAGING]: 'https://app-stage.buildingconnected.com/',
  [ENVS.PRODUCTION]: 'https://app.buildingconnected.com/',
};

export const PLANGRID_URLS = {
  [ENVS.QA]: 'https://plangrid-dev.planfront.net/',
  [ENVS.STAGING]: 'https://plangrid-test.planfront.net/',
  [ENVS.PRODUCTION]: 'https://app.plangrid.com/',
};

export const TRADETAPP_URLS = {
  [ENVS.QA]: 'https://tradetapp-app-dev.herokuapp.com/',
  [ENVS.STAGING]: 'https://tradetapp-app-staging.herokuapp.com/',
  [ENVS.PRODUCTION]: 'https://app.tradetapp.com/',
};

export const AUTOSPECS_URLS = {
  [ENVS.QA]: 'https://autospecs-dev.pype.io/?source=acc',
  [ENVS.STAGING]: 'https://autospecs-test.pype.io/?source=acc',
  [ENVS.PRODUCTION]: 'https://autospecs.pype.io/?source=acc',
};

export const SYSTEM_ENGINEERING_URLS = {
  [ENVS.QA]: {
    [REGIONS.US]: 'https://io.plangrid-dev.planfront.net',
    [REGIONS.EMEA]: '',
    [REGIONS.APAC]: '',
    [REGIONS.AUS]: '',
  },
  [ENVS.STAGING]: {
    [REGIONS.US]: 'https://io.plangrid-test.planfront.net',
    [REGIONS.EMEA]: '',
    [REGIONS.APAC]: 'https://io.aus-stg.planfront.net',
    [REGIONS.AUS]: 'https://io.aus-stg.planfront.net',
  },
  [ENVS.PRODUCTION]: {
    [REGIONS.US]: 'https://io.plangrid.com',
    [REGIONS.EMEA]: 'https://io.plangrid.be',
    [REGIONS.APAC]: 'https://io.plangrid.com.au',
    [REGIONS.AUS]: 'https://io.plangrid.com.au',
  },
};

export const USER_PREFERENCES_PATH = 'settings/v1/preference';
export const USER_PREFERENCES_PRODEX_BULK_PATH = 'settings/v1/preferences';
export const USER_PREFERENCES_DOMAIN = 'prodex';
export const USER_PREFERENCES_SETTING_NAME = 'product-entry-flow';
export const USER_PREFERENCES_SCOPE = 'user-project';

export const getUserPreferenceUrl = (
  scope = USER_PREFERENCES_SCOPE,
  setting = USER_PREFERENCES_SETTING_NAME,
) => {
  const env = getEnv();
  const region = getRegion();

  return `${SYSTEM_ENGINEERING_URLS[env][region]}/${USER_PREFERENCES_PRODEX_BULK_PATH}/${USER_PREFERENCES_DOMAIN}/${scope}/${setting}`;
};

export const putUserPreferenceUrl = (
  projectId?: string,
  scope = USER_PREFERENCES_SCOPE,
  setting = USER_PREFERENCES_SETTING_NAME,
) => {
  const env = getEnv();
  const region = getRegion();

  return `${SYSTEM_ENGINEERING_URLS[env][region]}/${USER_PREFERENCES_PATH}/${USER_PREFERENCES_DOMAIN}/${scope}/${projectId}/${setting}`;
};
